import fscreen from 'fscreen';
import Modal from './Modal';
import ModalBodyManager from './ModalBodyManager';
import ModalUtils from './ModalUtils';

class ModalStack {
  private stack: Array<Modal>;

  private activeModal: Modal | undefined;

  constructor() {
    this.stack = [];

    fscreen.addEventListener('fullscreenchange', this.onFullscreenChange);
  }

  onFullscreenChange = () => {
    const root = fscreen.fullscreenElement || document.body;

    this.stack.forEach((modal) => modal.attachTo(root));
  };

  getActiveModal(): Modal | undefined {
    return this.activeModal;
  }

  setActiveModal(modal: Modal) {
    if (modal === this.activeModal) {
      return;
    }

    if (this.activeModal) {
      this.activeModal.unsetActiveModal();
    }

    this.activeModal = modal;

    modal.setActiveModal();
  }

  addModal(modal: Modal) {
    this.stack.push(modal);

    if (this.activeModal) {
      modal.updateBaseZIndex(this.activeModal.getBaseZIndex() + 1);
    }

    this.setActiveModal(modal);
  }

  removeModal(modal: Modal) {
    modal.deactivateFocusTrap();

    const { stack } = this;

    const index = stack.indexOf(modal);
    const wasHighest = stack.length - 1 === index;

    stack.splice(index, 1);

    if (stack.length) {
      this.setActiveModal(stack[stack.length - 1]);
    } else {
      modal.unsetActiveModal();
      this.activeModal = undefined;
    }

    if (wasHighest) {
      modal.returnFocus();
    }
  }

  registerNewModal = (modal: Modal) => {
    const { options } = modal;

    options.beforeOpen = ModalUtils.wrapCallback(options.beforeOpen, () => this.beforeOpen(modal));
    options.beforeClose = ModalUtils.wrapCallback(options.beforeClose, () => this.beforeClose(modal));
    options.beforeRemove = ModalUtils.wrapCallback(options.beforeRemove, () => this.beforeRemove());

    if (this.stack.length === 0) {
      ModalBodyManager.prepForModalOpen();
    }
  };

  beforeOpen = (modal: Modal) => {
    this.addModal(modal);
  };

  beforeClose = (modal: Modal) => {
    this.removeModal(modal);
  };

  beforeRemove = () => {
    if (this.stack.length === 0) {
      ModalBodyManager.reset();
    }
  };
}

export default ModalStack;
